<app-main-layout>
  <div class="bg-gray-50 min-h-screen">
    <div class="py-6 px-4 sm:px-6 max-w-[1500px] mx-auto pb-10">

      <!-- Page header -->
      <div class="flex flex-row items-center justify-between gap-4 mb-5 flex-wrap">
        <div class="flex items-center gap-4 min-w-0">
          <button
            class="no-print h-10 w-10 rounded-full bg-white border border-gray-200 hover:border-amber-300 hover:bg-amber-50 text-gray-500 hover:text-amber-700 flex items-center justify-center shadow-sm transition-colors flex-shrink-0"
            (click)="onCancel()" matTooltip="Back to purchase orders" aria-label="Back to purchase orders">
            <mat-icon class="!text-[20px]">arrow_back</mat-icon>
          </button>
          <div class="min-w-0">
            <div class="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
              <a routerLink="/purchases" [queryParams]="listQuery" class="hover:text-amber-700 transition-colors">Purchase orders</a>
              <mat-icon class="!text-[14px] !h-3.5 !w-3.5">chevron_right</mat-icon>
              <span class="text-gray-500">{{ isEditMode ? 'Edit' : 'New' }}</span>
            </div>
            <div class="flex flex-row items-center gap-2.5 flex-wrap">
              <h1 class="text-2xl font-bold text-gray-900 tracking-tight">{{ isEditMode ? reference : 'New purchase order' }}</h1>
              @if (isEditMode) {
                <span [ngClass]="getBillingStatusClass(billingStatus)" class="rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize">{{ billingStatus | lowercase }}</span>
                @if (daysLate; as late) {
                  @if (late > 0) {
                    <span class="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-0.5"><mat-icon class="!text-[13px] !h-[13px] !w-[13px]">schedule</mat-icon>{{ late }} day{{ late === 1 ? '' : 's' }} late</span>
                  }
                }
              }
            </div>
          </div>
        </div>

        <div class="no-print flex flex-row items-center gap-2 flex-shrink-0">
          @if (isEditMode) {
            <div class="flex flex-row items-center bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              <button class="p-2 text-gray-600 hover:bg-amber-50 hover:text-amber-700 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                [disabled]="!previousPurchaseOrderId" (click)="goToPreviousPurchaseOrder()" matTooltip="Previous purchase order">
                <mat-icon>chevron_left</mat-icon>
              </button>
              <button class="p-2 text-gray-600 hover:bg-amber-50 hover:text-amber-700 disabled:opacity-30 disabled:hover:bg-transparent transition-colors border-l border-gray-200"
                [disabled]="!nextPurchaseOrderId" (click)="goToNextPurchaseOrder()" matTooltip="Next purchase order">
                <mat-icon>chevron_right</mat-icon>
              </button>
            </div>
            <button class="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-semibold hover:border-amber-300 hover:bg-amber-50 shadow-sm transition-colors" (click)="onNewPurchaseOrder()">
              <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">add</mat-icon> New PO
            </button>
          }
          <button class="flex items-center gap-1.5 pl-4 pr-2.5 py-2.5 rounded-xl bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-700 transition-colors" [matMenuTriggerFor]="poActionsMenu">
            Actions <mat-icon class="!text-amber-400">arrow_drop_down</mat-icon>
          </button>
          <mat-menu #poActionsMenu="matMenu" xPosition="before">
            <button mat-menu-item (click)="onPrint()"><mat-icon>print</mat-icon><span>Print PO</span></button>
            <button mat-menu-item (click)="onSend()"><mat-icon>send</mat-icon><span>Send to supplier</span></button>
            @if (isCancelled) {
              <button mat-menu-item (click)="setStatus(statusFlow[0].value)"><mat-icon>restart_alt</mat-icon><span>Reopen as draft</span></button>
            } @else {
              <button mat-menu-item (click)="setStatus('CANCELLED')"><mat-icon class="!text-red-500">block</mat-icon><span class="text-red-600">Mark as cancelled</span></button>
            }
          </mat-menu>
        </div>
      </div>

      @if (loading) {
        <div class="po-card min-h-80 flex flex-col justify-center items-center">
          <mat-spinner diameter="32"></mat-spinner>
          <div class="text-sm text-gray-500 mt-3">Loading purchase order&hellip;</div>
        </div>
      } @else {

        <!-- Status -->
        <div class="po-card !py-3 !px-4 mb-6 flex flex-row items-center gap-4 flex-wrap">
          @if (isCancelled) {
            <div class="flex items-center gap-3 flex-1 min-w-0">
              <span class="h-8 w-8 rounded-full bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">block</mat-icon></span>
              <div class="min-w-0">
                <div class="text-sm font-semibold text-red-700">This purchase order is cancelled</div>
                <div class="text-xs text-gray-500">It won't count towards incoming stock.</div>
              </div>
            </div>
            <button class="no-print text-xs font-semibold px-3.5 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 transition-colors" (click)="setStatus(statusFlow[0].value)">Reopen as draft</button>
          } @else {
            <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider pl-1 hidden md:block">Status</div>
            <div class="po-steps flex-1 overflow-x-auto">
              @for (step of statusFlow; track step.value; let i = $index; let last = $last) {
                <button type="button" class="po-step" [class.done]="i < statusIndex" [class.current]="i === statusIndex"
                  (click)="setStatus(step.value)" [matTooltip]="i === statusIndex ? 'Current status' : 'Set status to ' + step.label">
                  <span class="po-step-circle">
                    @if (i < statusIndex) { <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">check</mat-icon> } @else { {{ i + 1 }} }
                  </span>
                  {{ step.label }}
                </button>
                @if (!last) { <span class="po-step-line" [class.done]="i < statusIndex"></span> }
              }
            </div>
          }
        </div>

        <div class="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-6 items-start">

          <!-- ============ MAIN COLUMN ============ -->
          <div class="flex flex-col gap-6 min-w-0">

            <!-- Items -->
            <div class="po-card">
              <div class="flex flex-row items-center justify-between gap-3 mb-5 flex-wrap">
                <div class="flex items-center gap-3">
                  <span class="po-card-icon"><mat-icon class="!text-[20px] !h-5 !w-5">inventory_2</mat-icon></span>
                  <div>
                    <div class="font-bold text-gray-900 leading-tight">Items</div>
                    <div class="text-xs text-gray-400">{{ validLineCount }} line{{ validLineCount === 1 ? '' : 's' }} &middot; {{ totalOrdered }} unit{{ totalOrdered === 1 ? '' : 's' }} ordered</div>
                  </div>
                </div>
                <div class="no-print flex items-center gap-2">
                  @if (isEditMode && totalOrdered > 0 && totalReceived < totalOrdered) {
                    <button class="flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors" (click)="receiveAll()" matTooltip="Fill the Received column with everything ordered">
                      <mat-icon class="!text-[16px] !h-4 !w-4">done_all</mat-icon> Receive all
                    </button>
                  }
                  <button class="flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-xl bg-neutral-900 text-white hover:bg-neutral-700 transition-colors" (click)="addItem()">
                    <mat-icon class="!text-[16px] !h-4 !w-4 !text-amber-400">add</mat-icon> Add item
                  </button>
                </div>
              </div>

              @if (items.length === 0) {
                <div class="rounded-2xl border-2 border-dashed border-gray-200 py-12 px-6 flex flex-col items-center text-center">
                  <span class="h-12 w-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center"><mat-icon>add_shopping_cart</mat-icon></span>
                  <div class="font-semibold text-gray-800 mt-3">No items yet</div>
                  <div class="text-sm text-gray-400 mt-1 max-w-sm">Add the products you're ordering - search the catalogue or type a description for anything that isn't listed.</div>
                  <button class="no-print mt-4 flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-xl bg-neutral-900 text-white hover:bg-neutral-700 transition-colors" (click)="addItem()">
                    <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-amber-400">add</mat-icon> Add the first item
                  </button>
                </div>
              } @else {
                <div class="overflow-x-auto rounded-xl border border-gray-100">
                  <table class="po-table w-full text-sm">
                    <thead>
                      <tr class="text-left border-b border-gray-100">
                        <th class="py-3 pl-4 pr-1 w-8">#</th>
                        <th class="py-3 px-3 min-w-[280px]">Product</th>
                        <th class="py-3 px-2 w-24 text-right" matTooltip="Total stock across both yards">On hand</th>
                        <th class="py-3 px-2 w-24 text-right">Qty</th>
                        @if (showReceiptColumns) {
                          <th class="py-3 px-2 w-24 text-right">Received</th>
                          <th class="py-3 px-2 w-24 text-right">Billed</th>
                        }
                        <th class="py-3 px-2 w-32 text-right">Unit price</th>
                        <th class="py-3 px-2 w-24 text-right">Tax</th>
                        <th class="py-3 px-3 w-32 text-right">Amount</th>
                        <th class="no-print py-3 pr-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (item of items; track item.id; let i = $index) {
                        <tr class="po-row border-b border-gray-50 last:border-b-0 align-top transition-colors hover:bg-amber-50/30">
                          <td class="py-3 pl-4 pr-1 pt-[18px] text-xs font-semibold text-gray-300 po-tabular">{{ i + 1 }}</td>
                          <td class="py-3 px-3">
                            <app-product-picker
                              size="sm"
                              placeholder="Search products, or type a description"
                              panelWidth="min(560px, 92vw)"
                              noMatchHint="That's fine - it'll be kept as a custom line description"
                              [products]="products"
                              [selectedId]="item.productId"
                              [(query)]="item.productName"
                              (queryEdited)="onProductNameInput(item)"
                              (picked)="selectProduct(item, $event)"
                              />
                            <div class="mt-1.5 flex items-center gap-2">
                              <label class="text-[10px] font-semibold uppercase tracking-wider text-gray-400" [for]="'sku-' + item.id">SKU</label>
                              <input [id]="'sku-' + item.id" type="text" placeholder="-"
                                class="w-32 px-2 py-0.5 rounded-md border border-transparent bg-transparent text-xs font-mono text-gray-500 hover:border-gray-200 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none transition-all"
                                [(ngModel)]="item.sku" />
                              @if (item.productName.trim() && !item.productId) {
                                <span class="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" matTooltip="Not linked to a catalogue product - stock and cost price won't update for this line">
                                  <mat-icon class="!text-[12px] !h-3 !w-3">edit_note</mat-icon> Custom item
                                </span>
                              }
                            </div>
                          </td>
                          <td class="py-3 px-2 pt-[14px] text-right">
                            @if (getStockBreakdown(item); as stock) {
                              <span class="inline-block text-xs font-semibold rounded-full px-2 py-0.5 po-tabular" [ngClass]="stock.total > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'">{{ stock.total }}</span>
                              <div class="text-[10px] font-medium leading-tight mt-1 text-gray-400 whitespace-nowrap po-tabular" [matTooltip]="'Kettering ' + stock.k + ' · Markstey ' + stock.m">K {{ stock.k }} &middot; M {{ stock.m }}</div>
                            } @else {
                              <span class="text-gray-300">&mdash;</span>
                            }
                            @if (pendingStockChange(item); as change) {
                              <div class="text-[10px] font-semibold leading-tight mt-0.5 whitespace-nowrap" [ngClass]="change > 0 ? 'text-emerald-600' : 'text-red-500'" [matTooltip]="'Change to ' + store + ' stock when this PO is saved'">
                                {{ change > 0 ? '+' : '' }}{{ change }} on save
                              </div>
                            }
                          </td>
                          <td class="py-3 px-2"><input type="number" min="1" class="po-field po-sm po-num" [(ngModel)]="item.quantity" aria-label="Quantity ordered" /></td>
                          @if (showReceiptColumns) {
                            <td class="py-3 px-2"><input type="number" min="0" [max]="item.quantity" class="po-field po-sm po-num" [ngClass]="item.quantityReceived >= item.quantity && item.quantity > 0 ? '!border-emerald-300 !bg-emerald-50' : ''" [(ngModel)]="item.quantityReceived" aria-label="Quantity received" /></td>
                            <td class="py-3 px-2"><input type="number" min="0" [max]="item.quantity" class="po-field po-sm po-num" [(ngModel)]="item.quantityBilled" aria-label="Quantity billed" /></td>
                          }
                          <td class="py-3 px-2">
                            <div class="relative">
                              <span class="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">{{ currencySymbol(currency) }}</span>
                              <input type="number" min="0" step="0.01" class="po-field po-sm po-num !pl-6" [(ngModel)]="item.unitCost" aria-label="Unit price" />
                            </div>
                          </td>
                          <td class="py-3 px-2">
                            <div class="relative">
                              <input type="number" min="0" max="100" class="po-field po-sm po-num !pr-6" [(ngModel)]="item.taxRate" aria-label="Tax rate" />
                              <span class="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">%</span>
                            </div>
                          </td>
                          <td class="py-3 px-3 pt-[15px] text-right font-semibold text-gray-900 whitespace-nowrap po-tabular">{{ currencySymbol(currency) }}{{ getLineSubtotal(item) | number: '1.2-2' }}</td>
                          <td class="no-print py-3 pr-3 pt-[11px] text-center">
                            <button class="po-row-delete h-8 w-8 rounded-full text-gray-400 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-all" (click)="removeItem(item)" matTooltip="Remove line" aria-label="Remove line">
                              <mat-icon class="!text-[18px]">delete_outline</mat-icon>
                            </button>
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
                <button class="no-print po-add-line mt-3" (click)="addItem()">
                  <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">add</mat-icon> Add another line
                </button>
              }

              <!-- Notes + totals -->
              <div class="mt-6 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start">
                <div>
                  <label class="po-label" for="po-notes">Notes</label>
                  <textarea id="po-notes" rows="4" placeholder="Anything the supplier or the yard should know - delivery instructions, packing, etc." class="po-field resize-y" [(ngModel)]="notes"></textarea>
                </div>
                <div class="rounded-2xl bg-gray-50 border border-gray-100 px-5 py-4 text-sm po-tabular">
                  <div class="flex flex-row justify-between text-gray-500"><span>Subtotal</span><span class="text-gray-800">{{ currencySymbol(currency) }}{{ getSubtotal() | number: '1.2-2' }}</span></div>
                  <div class="flex flex-row justify-between text-gray-500 mt-1.5"><span>Tax</span><span class="text-gray-800">{{ currencySymbol(currency) }}{{ getTotalTax() | number: '1.2-2' }}</span></div>
                  <div class="flex flex-row justify-between items-baseline mt-3 pt-3 border-t border-gray-200">
                    <span class="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Total</span>
                    <span class="text-2xl font-bold text-gray-900">{{ currencySymbol(currency) }}{{ getGrandTotal() | number: '1.2-2' }}</span>
                  </div>
                  @if (totalCharges > 0) {
                    <div class="flex flex-row justify-between text-xs text-gray-400 mt-2 pt-2 border-t border-dashed border-gray-200" matTooltip="Freight, duty etc. - not part of the supplier's invoice, only used for landed cost">
                      <span>+ Landed charges</span><span>£{{ totalCharges | number: '1.2-2' }}</span>
                    </div>
                  }
                </div>
              </div>
            </div>
            <!-- Items -->

            <!-- Shipping & terms -->
            <div class="po-card">
              <div class="flex items-center gap-3 mb-5">
                <span class="po-card-icon"><mat-icon class="!text-[20px] !h-5 !w-5">directions_boat</mat-icon></span>
                <div><div class="font-bold text-gray-900 leading-tight">Shipping &amp; terms</div><div class="text-xs text-gray-400">Mainly for overseas suppliers</div></div>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                <div>
                  <label class="po-label" for="po-incoterms">Incoterms</label>
                  <select id="po-incoterms" class="po-field po-select" [(ngModel)]="incoterms">
                    <option value="">Not set</option>
                    @for (opt of incotermsOptions; track opt) {
                      <option [value]="opt.code">{{ opt.code }} - {{ opt.name }}</option>
                    }
                  </select>
                </div>
                <div>
                  <label class="po-label" for="po-terms">Payment terms</label>
                  <select id="po-terms" class="po-field po-select" [(ngModel)]="paymentTerms">
                    <option value="">Not set</option>
                    @for (opt of paymentTermsChoices; track opt) {
                      <option [value]="opt">{{ opt }}</option>
                    }
                  </select>
                </div>
                <div>
                  <label class="po-label" for="po-pol">Port of loading</label>
                  <select id="po-pol" class="po-field po-select" [(ngModel)]="portOfLoading">
                    <option value="">Not set</option>
                    @for (opt of portOfLoadingChoices; track opt) {
                      <option [value]="opt">{{ opt }}</option>
                    }
                  </select>
                </div>
                <div>
                  <label class="po-label" for="po-pod">Port of discharge</label>
                  <select id="po-pod" class="po-field po-select" [(ngModel)]="portOfDischarge">
                    <option value="">Not set</option>
                    @for (opt of portOfDischargeChoices; track opt) {
                      <option [value]="opt">{{ opt }}</option>
                    }
                  </select>
                </div>
              </div>
              @if (portOfLoading || portOfDischarge) {
                <div class="mt-4 flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded-xl px-3 py-2.5">
                  <mat-icon class="!text-[16px] !h-4 !w-4 !text-gray-400">sailing</mat-icon>
                  <span class="font-semibold text-gray-700">{{ portOfLoading || '?' }}</span>
                  <span class="flex-1 border-t border-dashed border-gray-300 min-w-6"></span>
                  <mat-icon class="!text-[14px] !h-3.5 !w-3.5 !text-gray-400">arrow_forward</mat-icon>
                  <span class="font-semibold text-gray-700">{{ portOfDischarge || '?' }}</span>
                </div>
              }
            </div>
            <!-- Shipping & terms -->

            <!-- Landed costs -->
            <div class="po-card">
              <div class="flex flex-row items-start justify-between gap-4 flex-wrap mb-5">
                <div class="flex items-start gap-3">
                  <span class="po-card-icon"><mat-icon class="!text-[20px] !h-5 !w-5">local_shipping</mat-icon></span>
                  <div>
                    <div class="font-bold text-gray-900 leading-tight">Landed costs</div>
                    <p class="text-xs text-gray-400 mt-0.5 max-w-xl">
                      Freight, duty and other charges on top of the goods, in £. They're shared across the lines so each product gets a landed unit cost - what one unit really cost by the time it reached the yard.
                    </p>
                  </div>
                </div>
                <button class="no-print flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-xl bg-white border border-gray-200 text-gray-700 hover:border-amber-300 hover:bg-amber-50 transition-colors" (click)="addCharge()">
                  <mat-icon class="!text-[16px] !h-4 !w-4">add</mat-icon> Add charge
                </button>
              </div>

              @if (charges.length === 0) {
                <button type="button" class="no-print w-full text-left flex items-center gap-3 text-sm text-gray-500 bg-gray-50 hover:bg-amber-50 border border-gray-100 hover:border-amber-200 rounded-xl px-4 py-3 transition-colors" (click)="addCharge()">
                  <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-gray-400">info</mat-icon>
                  <span>No charges yet - landed cost equals the unit price{{ currency !== 'GBP' ? ' converted to £' : '' }}. <span class="font-semibold text-amber-700">Add freight, duty&hellip;</span></span>
                </button>
              } @else {
                <div class="rounded-xl border border-gray-100 overflow-hidden">
                  <div class="hidden sm:grid grid-cols-[200px_minmax(0,1fr)_150px_40px] gap-2 px-3 py-2 bg-gray-50 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                    <span>Type</span><span>Description</span><span class="text-right">Amount</span><span></span>
                  </div>
                  @for (charge of charges; track $index) {
                    <div class="grid grid-cols-1 sm:grid-cols-[200px_minmax(0,1fr)_150px_40px] gap-2 px-3 py-2.5 border-t border-gray-50 first:border-t-0 items-center">
                      <select class="po-field po-sm po-select" [(ngModel)]="charge.chargeType" aria-label="Charge type">
                        @for (t of chargeTypeOptions; track t) {
                          <option [value]="t.value">{{ t.label }}</option>
                        }
                      </select>
                      <input type="text" placeholder="Description (optional)" class="po-field po-sm" [(ngModel)]="charge.description" aria-label="Charge description" />
                      <div class="relative">
                        <span class="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">£</span>
                        <input type="number" min="0" step="0.01" placeholder="0.00" class="po-field po-sm po-num !pl-6" [(ngModel)]="charge.amount" aria-label="Charge amount" />
                      </div>
                      <button class="h-8 w-8 rounded-full text-gray-400 hover:bg-red-50 hover:text-red-500 flex items-center justify-center justify-self-end transition-colors" (click)="removeCharge($index)" matTooltip="Remove charge" aria-label="Remove charge"><mat-icon class="!text-[18px]">delete_outline</mat-icon></button>
                    </div>
                  }
                  <div class="flex justify-between items-center px-3 py-2.5 bg-gray-50 border-t border-gray-100 text-sm po-tabular">
                    <span class="text-gray-500">Total charges</span>
                    <b class="text-gray-900">£{{ totalCharges | number: '1.2-2' }}</b>
                  </div>
                </div>
              }

              <!-- How the charges are shared -->
              <div class="mt-5 flex flex-row items-end gap-5 flex-wrap">
                <div class="min-w-[300px]">
                  <div class="po-label">Share charges</div>
                  <div class="po-seg">
                    @for (m of allocationMethods; track m.value) {
                      <button type="button" [class.active]="allocationMethod === m.value" [matTooltip]="m.hint" (click)="allocationMethod = m.value">{{ m.label }}</button>
                    }
                  </div>
                </div>
                @if (currency !== 'GBP') {
                  <div>
                    <div class="po-label">Exchange rate</div>
                    <div class="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
                      1 {{ currency }} = £
                      <input type="number" min="0" step="0.0001" class="po-field po-num !w-28 !py-2" [(ngModel)]="exchangeRate" aria-label="Exchange rate" />
                    </div>
                  </div>
                }
              </div>

              @if (landedPreview.fellBack) {
                <div class="mt-3 flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-3 py-2">
                  <mat-icon class="!text-[16px] !h-4 !w-4 flex-shrink-0">warning_amber</mat-icon>
                  <span>None of these lines has a weight, so the charges were shared by quantity instead. Add a weight per unit below to share by weight.</span>
                </div>
              }

              <!-- Result per line -->
              @if (validLineCount > 0) {
                <div class="mt-5 overflow-x-auto rounded-xl border border-gray-100">
                  <table class="po-table w-full text-sm po-tabular">
                    <thead>
                      <tr class="text-left">
                        <th class="py-2.5 px-3">Product</th>
                        <th class="py-2.5 px-2 text-right">Qty</th>
                        @if (allocationMethod === 'WEIGHT') { <th class="py-2.5 px-2 text-right">kg / unit</th> }
                        <th class="py-2.5 px-2 text-right">Goods / unit</th>
                        <th class="py-2.5 px-2 text-right">Share of charges</th>
                        <th class="py-2.5 px-3 text-right">Landed / unit</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (item of items; track item.id; let i = $index) {
                        @if (item.productName.trim() && landedPreview.rows[i]) {
                          <tr class="border-t border-gray-50">
                            <td class="py-2.5 px-3 text-gray-700 max-w-[320px] truncate" [title]="item.productName">{{ item.productName }}</td>
                            <td class="py-2.5 px-2 text-right text-gray-600">{{ item.quantity }}</td>
                            @if (allocationMethod === 'WEIGHT') {
                              <td class="py-2 px-2 text-right">
                                <input type="number" min="0" step="0.001" class="po-field po-sm po-num !w-24 ml-auto" [(ngModel)]="item.unitWeight" aria-label="Weight per unit" />
                              </td>
                            }
                            <td class="py-2.5 px-2 text-right text-gray-600">£{{ landedPreview.rows[i].goodsPerUnit | number: '1.2-2' }}</td>
                            <td class="py-2.5 px-2 text-right text-gray-600">£{{ landedPreview.rows[i].allocation | number: '1.2-2' }}</td>
                            <td class="py-2.5 px-3 text-right font-bold text-gray-900">£{{ landedPreview.rows[i].landedUnitCost | number: '1.2-4' }}</td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                </div>
              }

              <div class="mt-4 text-xs text-gray-500 flex items-start gap-1.5">
                <mat-icon class="!text-[16px] !h-4 !w-4 flex-shrink-0" [ngClass]="landedCostAppliedAt ? 'text-green-600' : 'text-gray-400'">{{ landedCostAppliedAt ? 'check_circle' : 'info' }}</mat-icon>
                @if (landedCostAppliedAt) {
                  <span>Landed costs were written to the products' Cost Price on {{ landedCostAppliedAt | date: 'dd MMM yyyy, HH:mm' }}. Saving again updates them.</span>
                } @else {
                  <span>When billing is set to Billed (or Overdue / Paid), each line's landed unit cost becomes that product's Cost Price.</span>
                }
              </div>
            </div>
            <!-- Landed costs -->

          </div>
          <!-- ============ /MAIN COLUMN ============ -->

          <!-- ============ SIDEBAR ============ -->
          <div class="flex flex-col gap-6">

            <!-- Summary -->
            <div class="rounded-[18px] bg-neutral-900 text-white p-6 shadow-sm po-tabular">
              <div class="flex items-center justify-between">
                <span class="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">Order total</span>
                <span class="text-[11px] font-semibold rounded-full bg-white/10 px-2 py-0.5 text-neutral-300">{{ currency }}</span>
              </div>
              <div class="text-3xl font-bold mt-1.5 tracking-tight">{{ currencySymbol(currency) }}{{ getGrandTotal() | number: '1.2-2' }}</div>
              <div class="mt-1 text-xs text-neutral-400">{{ validLineCount }} line{{ validLineCount === 1 ? '' : 's' }} &middot; {{ totalOrdered }} unit{{ totalOrdered === 1 ? '' : 's' }} &middot; incl. {{ currencySymbol(currency) }}{{ getTotalTax() | number: '1.2-2' }} tax</div>

              @if (isEditMode && totalOrdered > 0) {
                <div class="mt-5 pt-5 border-t border-white/10 flex flex-col gap-4">
                  <div>
                    <div class="flex justify-between text-xs mb-1.5"><span class="font-semibold text-neutral-200">Received</span><span class="text-neutral-400">{{ totalReceived }} / {{ totalOrdered }} &middot; {{ receivedPct }}%</span></div>
                    <div class="h-1.5 rounded-full bg-white/10 overflow-hidden"><div class="h-full rounded-full transition-all" [class]="receivedPct >= 100 ? 'bg-emerald-400' : 'bg-amber-400'" [style.width.%]="receivedPct"></div></div>
                  </div>
                  <div>
                    <div class="flex justify-between text-xs mb-1.5"><span class="font-semibold text-neutral-200">Billed</span><span class="text-neutral-400">{{ totalBilled }} / {{ totalOrdered }} &middot; {{ billedPct }}%</span></div>
                    <div class="h-1.5 rounded-full bg-white/10 overflow-hidden"><div class="h-full rounded-full transition-all" [class]="billedPct >= 100 ? 'bg-emerald-400' : 'bg-indigo-400'" [style.width.%]="billedPct"></div></div>
                  </div>
                </div>
              }
            </div>

            <!-- Supplier -->
            <div class="po-card">
              <div class="flex items-center justify-between mb-4">
                <div class="font-bold text-gray-900">Supplier <span class="text-red-500">*</span></div>
                <button type="button" class="no-print flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors" (click)="onNewSupplier()">
                  <mat-icon class="!text-[16px] !h-4 !w-4">person_add</mat-icon> New
                </button>
              </div>

              @if (getSelectedSupplier(); as supplier) {
                <div class="rounded-2xl border border-gray-100 bg-gradient-to-b from-gray-50 to-white p-4">
                  <div class="flex items-center gap-3">
                    <span class="h-11 w-11 rounded-xl bg-amber-400 text-neutral-900 font-bold text-sm flex items-center justify-center flex-shrink-0">{{ getSupplierInitials(supplier) }}</span>
                    <div class="min-w-0 flex-1">
                      <div class="font-semibold text-gray-900 truncate" [title]="supplier.name">{{ supplier.name }}</div>
                      @if (supplier.country || supplier.city) { <div class="text-xs text-gray-400 truncate">{{ supplier.city ? supplier.city + (supplier.country ? ', ' : '') : '' }}{{ supplier.country }}</div> }
                    </div>
                    <button type="button" class="no-print h-8 w-8 rounded-full text-gray-400 hover:bg-amber-50 hover:text-amber-700 flex items-center justify-center transition-colors flex-shrink-0" (click)="onOpenSupplier()" matTooltip="Open supplier" aria-label="Open supplier">
                      <mat-icon class="!text-[18px]">open_in_new</mat-icon>
                    </button>
                  </div>
                  <div class="mt-3 pt-3 border-t border-gray-100 flex flex-col gap-2 text-sm">
                    @if (supplier.email) {
                      <a class="flex items-center gap-2 text-gray-700 hover:text-amber-700 min-w-0" [href]="'mailto:' + supplier.email"><mat-icon class="!text-[16px] !h-4 !w-4 !text-gray-400 flex-shrink-0">mail</mat-icon><span class="truncate">{{ supplier.email }}</span></a>
                    } @else {
                      <span class="flex items-center gap-2 text-amber-700 text-xs"><mat-icon class="!text-[16px] !h-4 !w-4">warning_amber</mat-icon>No email on file - needed to send the PO</span>
                    }
                    @if (supplier.phone) {
                      <a class="flex items-center gap-2 text-gray-700 hover:text-amber-700" [href]="'tel:' + supplier.phone"><mat-icon class="!text-[16px] !h-4 !w-4 !text-gray-400">call</mat-icon>{{ supplier.phone }}</a>
                    }
                  </div>
                  <button type="button" class="no-print mt-3 w-full flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-xl border border-gray-200 bg-white text-gray-600 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-800 transition-colors" (click)="changeSupplier()">
                    <mat-icon class="!text-[16px] !h-4 !w-4">swap_horiz</mat-icon> Change supplier
                  </button>
                </div>
              } @else {
                <div class="relative">
                  <mat-icon class="absolute left-3 top-1/2 -translate-y-1/2 !text-gray-400 !text-[18px] !h-[18px] !w-[18px] pointer-events-none">search</mat-icon>
                  <input #supplierInput type="text" autocomplete="off" placeholder="Search by name, email, phone or country"
                    class="po-field !pl-10"
                    [value]="supplierSearchTerm"
                    [matAutocomplete]="supplierAuto"
                    (input)="supplierSearchTerm = $any($event.target).value; onSupplierSearchInput()" />
                </div>
                <mat-autocomplete #supplierAuto="matAutocomplete" class="po-ac-panel" [displayWith]="supplierDisplay"
                  [autoActiveFirstOption]="true" [hideSingleSelectionIndicator]="true" (optionSelected)="onSupplierPicked($event.option.value)">
                  @for (s of getFilteredSuppliers(supplierSearchTerm); track s.id) {
                    <mat-option [value]="s">
                      <div class="flex items-center gap-3 py-2">
                        <span class="h-8 w-8 rounded-lg bg-amber-100 text-amber-800 font-bold text-[11px] flex items-center justify-center flex-shrink-0">{{ getSupplierInitials(s) }}</span>
                        <div class="min-w-0 flex-1 leading-snug">
                          <div class="text-sm font-medium text-gray-900 truncate">@for (seg of highlight(s.name, supplierSearchTerm); track $index) {<span [class.po-mark]="seg.match">{{ seg.text }}</span>}</div>
                          @if (s.email || s.phone || s.country) {
                            <div class="text-[11px] text-gray-400 truncate">{{ supplierMeta(s) }}</div>
                          }
                        </div>
                        @if (s.openOrders) {
                          <span class="text-[10px] font-semibold text-blue-700 bg-blue-50 rounded-full px-2 py-0.5 whitespace-nowrap" matTooltip="Open purchase orders with this supplier">{{ s.openOrders }} open</span>
                        }
                      </div>
                    </mat-option>
                    } @empty {
                    <mat-option disabled>
                      <div class="py-3 text-center text-sm text-gray-500 whitespace-normal">No supplier matches "{{ supplierSearchTerm }}"</div>
                    </mat-option>
                  }
                  <mat-option value="NEW">
                    <div class="flex items-center gap-3 py-2 border-t border-gray-100 -mx-1 px-1 text-sm font-semibold text-amber-700">
                      <span class="h-8 w-8 rounded-lg border-2 border-dashed border-amber-300 flex items-center justify-center flex-shrink-0"><mat-icon class="!text-[16px] !h-4 !w-4">add</mat-icon></span>
                      <span class="truncate">Create a new supplier@if (supplierSearchTerm.trim()) { <span class="font-normal text-amber-800">"{{ supplierSearchTerm.trim() }}"</span> }</span>
                    </div>
                  </mat-option>
                </mat-autocomplete>
                @if (supplierSearchTerm) {
                  <div class="text-xs text-amber-700 mt-1.5 flex items-center gap-1"><mat-icon class="!text-[14px] !h-3.5 !w-3.5">info</mat-icon>Pick a supplier from the list to select them.</div>
                } @else {
                  <div class="text-xs text-gray-400 mt-1.5">{{ suppliers.length }} supplier{{ suppliers.length === 1 ? '' : 's' }} on file</div>
                }
              }
            </div>

            <!-- Order details -->
            <div class="po-card">
              <div class="font-bold text-gray-900 mb-4">Order details</div>

              <dl class="grid grid-cols-2 gap-3 mb-5">
                <div class="rounded-xl bg-gray-50 px-3 py-2.5 min-w-0">
                  <dt class="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Reference</dt>
                  <dd class="text-sm font-semibold mt-0.5 truncate" [ngClass]="reference ? 'text-gray-900' : 'text-gray-400 italic font-normal'">{{ reference || 'Assigned on save' }}</dd>
                </div>
                <div class="rounded-xl bg-gray-50 px-3 py-2.5 min-w-0">
                  <dt class="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Buyer</dt>
                  <dd class="text-sm font-semibold text-gray-900 mt-0.5 truncate" [title]="buyerName">{{ buyerName || '-' }}</dd>
                </div>
              </dl>

              <div class="flex flex-col gap-4">
                <div>
                  <div class="po-label">Deliver to</div>
                  <div class="po-seg">
                    @for (opt of storeOptions; track opt) {
                      <button type="button" [class.active]="store === opt" (click)="store = opt">
                        <mat-icon class="!text-[16px] !h-4 !w-4">warehouse</mat-icon>{{ opt }}
                      </button>
                    }
                  </div>
                </div>

                <div>
                  <div class="po-label">Currency</div>
                  <div class="po-seg">
                    @for (opt of currencyOptions; track opt) {
                      <button type="button" [class.active]="currency === opt" (click)="currency = opt">
                        <span class="text-gray-400">{{ currencySymbol(opt) }}</span>{{ opt }}
                      </button>
                    }
                  </div>
                </div>

                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="po-label" for="po-order-date">Order date</label>
                    <input id="po-order-date" type="date" class="po-field" [(ngModel)]="orderDate" />
                  </div>
                  <div>
                    <label class="po-label" for="po-expected">Expected</label>
                    <input id="po-expected" type="date" class="po-field" [ngClass]="expectedDeliveryHint?.warn ? '!border-red-300' : ''" [(ngModel)]="expectedDeliveryDate" />
                    @if (expectedDeliveryHint; as hint) {
                      <div class="text-[11px] mt-1 font-medium" [ngClass]="hint.warn ? 'text-red-600' : 'text-gray-400'">{{ hint.text }}</div>
                    }
                  </div>
                </div>

                <div>
                  <div class="po-label">Billing</div>
                  <div class="po-seg">
                    @for (b of billingChoices; track b.value) {
                      <button type="button" class="!px-1.5 !text-xs" [class.active]="billingStatus === b.value" (click)="billingStatus = b.value">
                        <span class="po-dot" [ngClass]="b.dot"></span>{{ b.label }}
                      </button>
                    }
                  </div>
                </div>

                <div>
                  <label class="po-label" for="po-doc">Supplier document</label>
                  <div class="relative">
                    <mat-icon class="absolute left-3 top-1/2 -translate-y-1/2 !text-gray-400 !text-[18px] !h-[18px] !w-[18px] pointer-events-none">receipt_long</mat-icon>
                    <input id="po-doc" type="text" placeholder="e.g. INVOICE#07" class="po-field !pl-10" [(ngModel)]="sourceDocument" />
                  </div>
                </div>
              </div>
            </div>

          </div>
          <!-- ============ /SIDEBAR ============ -->

        </div>
      }

    </div>

    <!-- Sticky save bar -->
    @if (!loading) {
      <div class="no-print sticky bottom-0 z-20 bg-white/90 backdrop-blur border-t border-gray-200 shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.08)]">
        <div class="max-w-[1500px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div class="hidden sm:flex items-center gap-3 text-sm min-w-0">
            <span class="text-gray-400 truncate">{{ isEditMode ? 'Editing ' + reference : 'New purchase order' }}</span>
            @if (saveBlockers.length) {
              @for (b of saveBlockers; track b) {
                <span class="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 whitespace-nowrap"><mat-icon class="!text-[14px] !h-3.5 !w-3.5">radio_button_unchecked</mat-icon>{{ b }}</span>
              }
            } @else {
              <span class="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 whitespace-nowrap"><mat-icon class="!text-[14px] !h-3.5 !w-3.5">check_circle</mat-icon>Ready to save</span>
            }
          </div>
          <div class="flex items-center gap-3 ml-auto">
            <div class="text-right leading-tight mr-1 po-tabular">
              <div class="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Total</div>
              <div class="text-base font-bold text-gray-900 whitespace-nowrap">{{ currencySymbol(currency) }}{{ getGrandTotal() | number: '1.2-2' }}</div>
            </div>
            <button class="px-5 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors" (click)="onCancel()">Cancel</button>
            <button
              class="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 font-bold text-sm shadow-md shadow-amber-200 hover:from-amber-300 hover:to-amber-400 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              [disabled]="saving" (click)="onSave()">
              @if (saving) { <mat-spinner diameter="16"></mat-spinner> } @else { <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">check</mat-icon> }
              {{ saving ? 'Saving…' : (isEditMode ? 'Save changes' : 'Create purchase order') }}
            </button>
          </div>
        </div>
      </div>
    }
  </div>
</app-main-layout>
